import React, { useState, useEffect, Fragment } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import './DragonDrop.scss';
import ActivityBadge from '../badges/ActivityBadge';
import { useBadge } from '../../../customHooks/useBadge';
import ActivityCompletionNotice from '../../activities/ActivityCompletionNotice';
import { VideoGateContext } from '../../admin-page/pages/facilitator/VideoGateContext';

//Funcitonal wrapper around class-based, DragonDrop component so that the custom hook useBadge can be used.

const StrictModeDroppable = ({ children, ...props }) => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const animationFrame = requestAnimationFrame(() => {
      setEnabled(true);
    });

    return () => {
      cancelAnimationFrame(animationFrame);
      setEnabled(false);
    };
  }, []);

  if (!enabled) {
    return null;
  }

  return <Droppable {...props}>{children}</Droppable>;
};

const DragonDropWrapper = (props) => {
  const [{ showBadge }, dispatch] = useBadge(false);
  const { registerActivityRequirement, markActivityCompleted } =
    React.useContext(VideoGateContext);

  useEffect(() => {
    registerActivityRequirement();
  }, [registerActivityRequirement]);

  return (
    <DragonDrop
      content={props.content}
      badge={props.badge}
      dispatch={dispatch}
      showBadge={showBadge}
      markActivityCompleted={markActivityCompleted}
    />
  );
};

class DragonDrop extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      ...this.props.content,
      lockedMinHeight: null,
      complete: false,
    };
    this.columnAreaRefs = {};
  }

  componentDidMount() {
    this.lockColumnHeight();
  }

  componentDidUpdate() {
    if (!this.state.lockedMinHeight) {
      this.lockColumnHeight();
    }

    if (
      this.state.complete &&
      this.props.markActivityCompleted &&
      !this.reportedComplete
    ) {
      this.reportedComplete = true;
      this.props.markActivityCompleted();
    }
  }

  lockColumnHeight = () => {
    requestAnimationFrame(() => {
      const heights = Object.values(this.columnAreaRefs)
        .map((node) => node?.offsetHeight || 0)
        .filter(Boolean);

      if (!heights.length) {
        return;
      }

      const tallestColumnHeight = Math.max(...heights);
      if (
        tallestColumnHeight &&
        tallestColumnHeight !== this.state.lockedMinHeight
      ) {
        this.setState({
          lockedMinHeight: tallestColumnHeight,
        });
      }
    });
  };

  getIsComplete = (columns) => {
    const sourceColumnId = this.state.columnOrder?.[0];
    const startColumn = columns[sourceColumnId];

    return startColumn ? startColumn.optionIds.length === 0 : false;
  };

  onDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) {
      return;
    }

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const start = this.state.columns[source.droppableId];
    const finish = this.state.columns[destination.droppableId];

    // if the ptype are matched, do not allow user to drag the option to wrong area.
    if (result.draggableId && finish) {
      const selectedItemType = this.state.options[result.draggableId].ptype;
      const droppedAreaType = finish.title;
      if (
        selectedItemType.toLowerCase() !== droppedAreaType.toLowerCase() &&
        droppedAreaType.toLowerCase() !== 'start'
      ) {
        setTimeout(() => {
          this.setState({
            showErrorMessage: true,
          });
        }, 0);

        setTimeout(() => {
          this.setState({
            showErrorMessage: false,
          });
        }, 2000);

        return;
      }
    }

    if (start === finish) {
      const newTaskIds = Array.from(start.optionIds);
      newTaskIds.splice(source.index, 1);
      newTaskIds.splice(destination.index, 0, draggableId);

      const newColumn = {
        ...start,
        optionIds: newTaskIds,
      };

      const newState = {
        ...this.state,
        columns: {
          ...this.state.columns,
          [newColumn.id]: newColumn,
        },
        complete: this.getIsComplete({
          ...this.state.columns,
          [newColumn.id]: newColumn,
        }),
      };

      this.setState(newState);
      return;
    }

    // Moving from one list to another
    this.props.dispatch({ type: 'show', payload: this.props.badge });
    const startTaskIds = Array.from(start.optionIds);
    startTaskIds.splice(source.index, 1);
    const newStart = {
      ...start,
      optionIds: startTaskIds,
    };

    const finishTaskIds = Array.from(finish.optionIds);
    finishTaskIds.splice(destination.index, 0, draggableId);
    const newFinish = {
      ...finish,
      optionIds: finishTaskIds,
    };

    const newState = {
      ...this.state,
      columns: {
        ...this.state.columns,
        [newStart.id]: newStart,
        [newFinish.id]: newFinish,
      },
      complete: this.getIsComplete({
        ...this.state.columns,
        [newStart.id]: newStart,
        [newFinish.id]: newFinish,
      }),
    };
    this.setState(newState);
  };

  render() {
    return (
      <DragDropContext onDragEnd={this.onDragEnd}>
        {/* <div
          className={`error-pop ${
            this.state.showErrorMessage ? 'open' : 'closed'
          }`}
        >
          <p>Try Again!</p>
        </div> */}
        {/* <div
          className='dragon-error-note'
          style={{ opacity: this.state.showErrorMessage ? 1 : 0 }}
        >
          Wrong Choice!
        </div> */}
        <div className='dragon-act-wrap'>
          <div
            className={`error-pop ${
              this.state.showErrorMessage ? 'open' : 'closed'
            }`}
          >
            <p>Try Again!</p>
          </div>
          {this.state.columnOrder.map((columnId, i) => {
            const column = this.state.columns[columnId];
            const options = column.optionIds.map(
              (optionId) => this.state.options[optionId]
            );

            return (
              <div key={i} className={`column ${columnId}`}>
                <h1>{column.title}</h1>
                <StrictModeDroppable droppableId={column.id} type='TASK'>
                  {(provided, snapshot) => (
                    <div
                      className='draggable-area'
                      style={{
                        backgroundColor: snapshot.isDraggingOver
                          ? 'lightgrey'
                          : 'white',
                        minHeight: this.state.lockedMinHeight
                          ? `${this.state.lockedMinHeight}px`
                          : undefined,
                      }}
                      data-column-id={column.id}
                      ref={(node) => {
                        provided.innerRef(node);
                        this.columnAreaRefs[column.id] = node;
                      }}
                      {...provided.droppableProps}
                    >
                      {options.map((option, index) => (
                        <Draggable
                          draggableId={option.id}
                          index={index}
                          key={option.id}
                        >
                          {(provided, snapshot) => (
                            <div
                              className='dragon-button'
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              ref={provided.innerRef}
                            >
                              {option.content}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </StrictModeDroppable>
              </div>
            );
          })}
        </div>
        {this.state.complete && <ActivityCompletionNotice />}
        {this.props.badge && (
          <ActivityBadge
            showBadge={this.props.showBadge}
            content={this.props.badge}
            dispatch={this.props.dispatch}
          />
        )}
      </DragDropContext>
    );
  }
}

export { DragonDropWrapper };
